import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPorAsins } from "../porAsinList/porAsinListSlice";
import { RootState } from "../app/rootReducer";
import {
  Table,
  CollectionPreferences,
  Pagination,
  Header,
  Popover,
  StatusIndicator,
  Spinner,
  SpaceBetween,
  Link,
} from "@amzn/awsui-components-react/polaris";
import { fetchEditableAttributes } from "../porAsinList/porAsinListSlice";
import { SortFacet } from "../../from-server/asin-central/search";
import {
  baselinePageSelectorOptions,
  baselineTableColumnDefinitions,
  DATE_BASELINE_COLUMN_DEFINITIONS,
  EDITABLE_DATE_BASELINE_COLUMN_DEFINITIONS,
} from "./baselineTable-config";
import {
  setPreferences,
  mergeVisibleContentPreferences,
  setSort,
  setBaselinePageNumber,
  setViewingBaselineTable,
  setEditingBaselineTable,
  fetchGetCandidateDates,
  fetchRecordAsinMarketplaceDates,
  fetchBaselineRequest,
} from "./baselineTableConnector";
import {
  defaultSort,
  TableHeaderMessage,
  EmptyTableState,
} from "../porAsinList/TableCommonElements";
import { writeToLocalStorage } from "../../util/localStorageUtils";
import { Button } from "@amzn/awsui-components-react";
import { trackButtonClick, trackComponentView } from "../telemetry/telemetry";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../from-server/asin-central/model";
import {
  clearNotifications,
  createNotificationDefinition,
} from "../notification/notificationConnector";
import {
  clearSelectedAnnounceDates,
  clearSelectedOnlineStreetDates,
  SelectedAsinMarketplaceDate,
  startGetCandidateDates,
} from "./candidateDatesConnector";
import { AsinMarketplace } from "@amzn/coral_com-amazon-asincentral-common/dist/umd/AsinMarketplace";
import { AsinMarketplaceDate } from "@amzn/coral_com-amazon-deviceslaunchcontrolservice/dist/umd/AsinMarketplaceDate";
import { AsinMarketplaceDates } from "@amzn/coral_com-amazon-deviceslaunchcontrolservice/dist/umd/AsinMarketplaceDates";
import {
  LaunchControlDate,
  LaunchControlDateDates,
} from "@amzn/coral_com-amazon-deviceslaunchcontrolservice";
import { AuthorizedAttribute } from "../../from-server/brass/model";
import { isNoFilterApplied } from "../searchSideBar/queryStringHelpers";
import {
  PropertyFilter,
  PropertyFilterGroup,
  setQueryMode,
  setQueryPropertyFilters,
  setQueryPropertyFiltersForceRehydrate,
  setQueryString,
} from "../searchSideBar/searchSideBarConnector";
import { NoFiltersPanel } from "../validation/NoFiltersPanel";
import { MilestoneScheduleTable } from "./milestoneScheduleTable";
import { currentPagePorAsinsBelongToSameProgram } from "../common/PorAsinListUtils";

const BASELINE_STATUS_ATTRIBUTE_KEY = "baselineStatus";
const BASELINE_ID_ATTRIBUTE_KEY = "baselineId";
const DEFAULT_BASELINE_STATUS_VALUES: readonly string[] = [
  "ANNOUNCE_DATE_NON_COMPLIANT",
  "ONLINE_STREET_DATE_NON_COMPLIANT",
];

/**
 * Recursively true when any node in `group` matches `propertyKey`.
 * Used by the auto-seed effect to detect both `baselineStatus` (so
 * we don't override an explicit user choice) and `baselineId` (so
 * we don't hide already-baselined ASINs on the deep-link path).
 */
function containsPropertyFilterForKey(
  group: PropertyFilterGroup,
  propertyKey: string
): boolean {
  return group.filters.some((f) => {
    if (f.type === "PropertyFilter") {
      return f.propertyKey === propertyKey;
    }
    return containsPropertyFilterForKey(f, propertyKey);
  });
}

/**
 * Returns a fresh PropertyFilterGroup with an OR sub-group holding
 * the two default `baselineStatus` chips appended to the nested
 * attribute group. Preserves every existing root filter and every
 * existing attribute filter. `current` is never mutated. Matches how
 * the Cloudscape PropertyFilter serializes a multi-value selection
 * for a single property, so the chips render as two independent
 * tokens the user can remove separately.
 */
function withDefaultBaselineStatusFilters(
  current: PropertyFilterGroup
): PropertyFilterGroup {
  const defaultsOrGroup: PropertyFilterGroup = {
    type: "PropertyFilterGroup",
    operation: "or",
    filters: DEFAULT_BASELINE_STATUS_VALUES.map(
      (value): PropertyFilter => ({
        type: "PropertyFilter",
        operator: "filter",
        propertyKey: BASELINE_STATUS_ATTRIBUTE_KEY,
        value,
      })
    ),
  };

  const attributeGroupIndex = current.filters.findIndex(
    (f): f is PropertyFilterGroup => f.type === "PropertyFilterGroup"
  );

  if (attributeGroupIndex === -1) {
    // No existing attribute group; AND the defaults with whatever
    // else is at the root. If the root is already `"and"` we can
    // append flat; if it's `"or"` we wrap the whole root under a
    // fresh `"and"` so the defaults narrow the result set instead
    // of widening it.
    const defaultsAttributeGroup: PropertyFilterGroup = {
      type: "PropertyFilterGroup",
      operation: "and",
      filters: [defaultsOrGroup],
    };
    if (current.operation === "and") {
      return {
        type: "PropertyFilterGroup",
        operation: "and",
        filters: [...current.filters, defaultsAttributeGroup],
      };
    }
    return {
      type: "PropertyFilterGroup",
      operation: "and",
      filters: [current, defaultsAttributeGroup],
    };
  }

  const attributeGroup = current.filters[
    attributeGroupIndex
  ] as PropertyFilterGroup;
  // AND-combine the defaults with the existing attribute filters so
  // the tab is scoped to non-compliant ASINs. When the existing
  // group is already `"and"` we can flatten the defaults in; when
  // it's `"or"` we must preserve that sub-group whole and wrap both
  // it and the defaults under a fresh `"and"` group, otherwise the
  // defaults would OR with the user's filters (widening the result
  // instead of narrowing it).
  const nextAttributeGroup: PropertyFilterGroup =
    attributeGroup.operation === "and"
      ? {
          type: "PropertyFilterGroup",
          operation: "and",
          filters: [...attributeGroup.filters, defaultsOrGroup],
        }
      : {
          type: "PropertyFilterGroup",
          operation: "and",
          filters: [attributeGroup, defaultsOrGroup],
        };

  const nextRootFilters = [...current.filters];
  nextRootFilters[attributeGroupIndex] = nextAttributeGroup;

  return {
    type: "PropertyFilterGroup",
    operation: current.operation,
    filters: nextRootFilters,
  };
}

// Once-per-session guard so the user can remove the auto-applied
// chips and not have them re-inserted every time they revisit the
// tab. A component-local ref would reset on unmount and re-fire on
// re-entry.
let hasAutoAppliedBaselineStatusDefaults = false;

const BaselineActionButtons = () => {
  return (
    <>
      <BaselineActionButton />
    </>
  );
};

export const BaselineEditActionButtons = () => {
  const dispatch = useDispatch();
  const {
    selectedAnnounceDates,
    selectedOnlineStreetDates,
    programId,
    scheduleId,
    isGettingCandidates,
  } = useSelector((state: RootState) => state.candidateDates);
  const { existingBaselineRequest } = useSelector(
    (state: RootState) => state.asinBaselineTableControl
  );

  useEffect(() => {
    trackComponentView("baselineTable", "table");
  }, []);

  return (
    <div data-testid="baseline-edit-actions">
      {isGettingCandidates && <Spinner size="big" />}
      <Button
        data-testid="baseline-cancel-button"
        children="Cancel"
        disabled={isGettingCandidates}
        onClick={() => {
          trackButtonClick("baselineTable", "cancel", {});
          dispatch(setViewingBaselineTable());
          dispatch(clearSelectedAnnounceDates());
          dispatch(clearSelectedOnlineStreetDates());
        }}
      />
      &nbsp;&nbsp;&nbsp;
      <Button
        data-testid="baseline-save-button"
        children="Save"
        variant="primary"
        disabled={isGettingCandidates}
        onClick={() => {
          trackButtonClick("baselineTable", "save", {
            announceDateCount: selectedAnnounceDates.length,
            onlineStreetDateCount: selectedOnlineStreetDates.length,
          });
          const datesToRecord: AsinMarketplaceDates = new AsinMarketplaceDates(
            Object.keys(selectedAnnounceDates).map((asinMp) => {
              // TODO: Eventually refactor if we want to allow baselining single date types, asssuming that announce and street date are always both selected
              const selectedAnnounceDate: SelectedAsinMarketplaceDate =
                selectedAnnounceDates[asinMp];
              const selectedOnlineStreetDate: SelectedAsinMarketplaceDate =
                selectedOnlineStreetDates[asinMp];
              const combinedDates = selectedAnnounceDate.dates.concat(
                selectedOnlineStreetDate.dates
              );

              return AsinMarketplaceDate.clone({
                asin: selectedAnnounceDate.asin,
                obfuscatedMarketplaceId:
                  selectedAnnounceDate.obfuscatedMarketplaceId,
                dates: new LaunchControlDateDates(
                  combinedDates.map((date) =>
                    LaunchControlDate.clone({
                      dateType: date.dateType,
                      dateValue: date.dateValueInMillis,
                      status: date.dateStatus,
                    })
                  )
                ),
              });
            })
          );
          const dateTypes = datesToRecord.flatMap((d) =>
            d.dates.map((x) => x.dateType)
          );
          dispatch(
            fetchRecordAsinMarketplaceDates(
              datesToRecord,
              [...new Set(dateTypes)],
              scheduleId,
              programId,
              existingBaselineRequest
            )
          );
          dispatch(
            createNotificationDefinition(
              "success",
              "Baseline Request Sent",
              true,
              `Request to baseline ${datesToRecord.length} dates sent.`
            )
          );
          dispatch(clearSelectedAnnounceDates());
          dispatch(clearSelectedOnlineStreetDates());
          dispatch(setViewingBaselineTable());
        }}
      />
    </div>
  );
};

function isButtonDisabled(
  asins: (FullAsinWithAudits | RestrictedAsin)[],
  isLoading: boolean
) {
  return (
    asins.every((item) => item.isRestricted) || asins.length < 1 || isLoading
  );
}

const BaselineActionButton = () => {
  const dispatch = useDispatch();
  const { currentPagePorAsins, isLoading, editableAttributes } = useSelector(
    (state: RootState) => state.porAsinList
  );

  function userIsAuthorized(editableAttributes: AuthorizedAttribute[]) {
    return editableAttributes.some(
      (attribute) =>
        attribute.attributeName === "date_baseline" && attribute.authorized
    );
  }

  function getPopoverContent() {
    if (!userIsAuthorized(editableAttributes)) {
      return (
        <span>
          You must be a member of the{" "}
          <a
            href="https://permissions.amazon.com/a/team/ASIN-Central-Date-Baseline"
            target="_blank"
            rel="noreferrer"
          >
            "ASIN-Central-Date-Baseline"
          </a>{" "}
          team to perform date baselining
        </span>
      );
    }
    if (!currentPagePorAsinsBelongToSameProgram(currentPagePorAsins)) {
      return (
        <span>
          There are restricted ASINs or ASINs from different programs in the
          current page. Please refine your search criteria and try again
        </span>
      );
    }
  }

  return (
    <div data-testid="baseline-action-button-wrapper">
      {(!userIsAuthorized(editableAttributes) ||
        !currentPagePorAsinsBelongToSameProgram(currentPagePorAsins)) && (
        <Popover
          dismissButton={true}
          header=""
          position="top"
          size="medium"
          triggerType="custom"
          content={getPopoverContent()}
        >
          <StatusIndicator type="info" />
        </Popover>
      )}
      <Button
        data-testid="baseline-button"
        variant="primary"
        children="Baseline"
        disabled={
          isButtonDisabled(currentPagePorAsins, isLoading) ||
          !currentPagePorAsinsBelongToSameProgram(currentPagePorAsins) ||
          !userIsAuthorized(editableAttributes)
        }
        onClick={() => {
          trackButtonClick("baselineTable", "baseline", {});
          // TODO: Clear candidate data
          dispatch(
            createNotificationDefinition(
              "info",
              "Getting Candidate Dates",
              true,
              "Getting candidate dates..."
            )
          );
          dispatch(startGetCandidateDates());

          function getCandidateDates(
            asins: (FullAsinWithAudits | RestrictedAsin)[]
          ) {
            // Remove restricted ASINs from list
            const listOfAsinMps = asins
              .filter((x) => !x.isRestricted)
              .map((x) => x as FullAsinWithAudits);

            dispatch(
              fetchGetCandidateDates(
                listOfAsinMps.map(
                  (asinMp) =>
                    new AsinMarketplace(
                      asinMp.asin,
                      asinMp.obfuscatedMarketplaceId
                    )
                )
              )
            );
          }

          getCandidateDates(currentPagePorAsins);
          dispatch(clearNotifications());
          dispatch(
            setEditingBaselineTable({
              currentPagePorAsins,
            })
          );
        }}
      />
    </div>
  );
};

export const BaselineTable = () => {
  const dispatch = useDispatch();
  const { currentPagePorAsins, isLoading, count, isGettingEditableAttributes } =
    useSelector((state: RootState) => state.porAsinList);
  const {
    isEditing,
    editableBaselineAsins,
    preferences,
    sort,
    descending,
    pageNumber,
    existingBaselineRequest,
  } = useSelector((state: RootState) => state.asinBaselineTableControl);
  const {
    queryMode,
    queryString,
    queryPropertyFilters,
    activePropertyFilters,
    activePropertyFiltersVisibilityEnabled,
  } = useSelector((state: RootState) => state.generateQuery);

  const queryParameters = useSelector(
    (state: RootState) => state.generateQuery.queryParameters
  );

  const noFilterApplied = isNoFilterApplied(queryString);

  useEffect(() => {
    const baselineId = queryParameters["baselineId"]?.value as
      | string
      | undefined;
    if (baselineId && !existingBaselineRequest) {
      dispatch(fetchBaselineRequest(baselineId));
    }
  }, [queryParameters]);

  // Seed the tab with `baselineStatus IN (Needs Review …)` chips when
  // the user arrives with other filters applied but no status filter
  // of their own. Skipped in the no-filter zero state (so the
  // NoFiltersPanel still guides them) and skipped when they already
  // have a status chip (they picked one intentionally). Fires at most
  // once per session so removed chips don't re-appear.
  useEffect(() => {
    if (hasAutoAppliedBaselineStatusDefaults) {
      return;
    }
    // Legacy UIFilter mode keeps filters in `queryParameters`, not in
    // `queryPropertyFilters` — including baselineId deep links. Our
    // guards and seed all read `queryPropertyFilters`, so running in
    // UIFilter mode would silently miss the user's filters and rewrite
    // the applied query to only the two status defaults. FilterBar
    // snaps queryMode back to PropertyFilter on the shared routes;
    // this guard just waits for that to land.
    if (queryMode !== "PropertyFilter") {
      return;
    }
    if (noFilterApplied) {
      return;
    }
    if (
      containsPropertyFilterForKey(
        queryPropertyFilters,
        BASELINE_STATUS_ATTRIBUTE_KEY
      )
    ) {
      return;
    }
    // Baseline-request deep links pin a baselineId chip so a reviewer
    // can inspect the ASINs on that request. Seeding the Non-Compliant
    // defaults would filter out already-baselined rows — the ones the
    // reviewer opened the link to look at — so skip the auto-apply on
    // that path.
    if (
      containsPropertyFilterForKey(
        queryPropertyFilters,
        BASELINE_ID_ATTRIBUTE_KEY
      )
    ) {
      return;
    }
    hasAutoAppliedBaselineStatusDefaults = true;
    const nextFilters = withDefaultBaselineStatusFilters(queryPropertyFilters);
    // Same dispatch order as NoFiltersPanel's quick-filter buttons:
    // ensure PropertyFilter mode, publish the augmented tree, bump
    // the rehydrate signal so PropertyFilterInput repaints Polaris
    // from the new tree, and recompute queryString + reset paging.
    dispatch(setQueryMode("PropertyFilter"));
    dispatch(setQueryPropertyFilters(nextFilters));
    dispatch(setQueryPropertyFiltersForceRehydrate(true));
    dispatch(setQueryString());
    dispatch(setBaselinePageNumber({ pageNumber: 1 }));
  }, [dispatch, noFilterApplied, queryMode, queryPropertyFilters]);

  const getSort = (): SortFacet[] =>
    sort === undefined
      ? defaultSort
      : //TODO: Uncomment this when the backend is able to sort on this attribute.
        // [
        //     {
        //       attributeName: "baselineStatus",
        //       ascending: true,
        //     },
        //   ]
        [
          {
            attributeName: sort?.sortingField,
            ascending: !descending,
          },
        ];

  useEffect(() => {
    if (noFilterApplied) {
      return;
    }
    const offset = preferences.pageSize * (pageNumber - 1);
    dispatch(
      fetchPorAsins(offset, preferences.pageSize, queryString, getSort())
    );
  }, [
    pageNumber,
    queryString,
    preferences.pageSize,
    dispatch,
    sort,
    descending,
    noFilterApplied,
  ]);

  useEffect(() => {
    if (activePropertyFiltersVisibilityEnabled) {
      dispatch(
        mergeVisibleContentPreferences({
          activeColumns: Object.keys(activePropertyFilters ?? {}),
        })
      );
    }
  }, [queryString, activePropertyFiltersVisibilityEnabled]);

  useEffect(() => {
    if (!isGettingEditableAttributes) {
      dispatch(fetchEditableAttributes());
    }
  });

  if (noFilterApplied) {
    return (
      <div data-testid="baseline-table-wrapper">
        <NoFiltersPanel
          dataNoun="date baseline data"
          onResetPagination={() =>
            dispatch(setBaselinePageNumber({ pageNumber: 1 }))
          }
        />
      </div>
    );
  }

  return (
    <SpaceBetween size="l" data-testid="baseline-table-wrapper">
      <MilestoneScheduleTable />
      <Table
        data-testid="baseline-table"
        header={
          <Header
            data-testid="baseline-header"
            description={TableHeaderMessage(
              (pageNumber - 1) * preferences.pageSize,
              currentPagePorAsins.length,
              count
            )}
            actions={
              isEditing ? (
                <BaselineEditActionButtons />
              ) : (
                <BaselineActionButtons />
              )
            }
            info={
              <Link
                variant="info"
                target="_blank"
                rel="noreferrer"
                href="https://w.amazon.com/bin/view/PSLM/Working_with_us/ASIN_Central#HASINDateBaselineCentral"
              >
                Info
              </Link>
            }
          >
            Date Baseline
          </Header>
        }
        loading={isLoading}
        loadingText={"Loading"}
        empty={EmptyTableState(
          "No ASINs Currently Need Baseline",
          'Someone might have baselined these ASINs already. To see more ASINs, remove the "Baseline Status" or "Baseline Id" filter or change the search criteria'
        )}
        columnDefinitions={
          isEditing
            ? EDITABLE_DATE_BASELINE_COLUMN_DEFINITIONS
            : DATE_BASELINE_COLUMN_DEFINITIONS
        }
        items={isEditing ? editableBaselineAsins : currentPagePorAsins}
        stickyHeader={true}
        sortingColumn={sort}
        sortingDescending={descending}
        onSortingChange={(event) => dispatch(setSort(event.detail))}
        pagination={
          isEditing ? undefined : (
            <Pagination
              data-testid="baseline-pagination"
              pagesCount={Math.ceil(count / preferences.pageSize)}
              currentPageIndex={pageNumber}
              disabled={isLoading}
              onChange={({ detail }) => {
                dispatch(
                  setBaselinePageNumber({
                    pageNumber: detail.currentPageIndex,
                  })
                );
              }}
            />
          )
        }
        visibleColumns={preferences.visibleContent}
        preferences={
          isEditing ? undefined : (
            <CollectionPreferences
              data-testid="baseline-preferences"
              onConfirm={(event) => {
                writeToLocalStorage(
                  "BASELINE_TABLE_PREFERENCES",
                  JSON.stringify(event.detail)
                );
                dispatch(setPreferences(event.detail));
              }}
              title={"Preferences"}
              confirmLabel={"Confirm"}
              cancelLabel={"Cancel"}
              disabled={isLoading}
              preferences={preferences}
              pageSizePreference={baselinePageSelectorOptions}
              visibleContentPreference={baselineTableColumnDefinitions}
            />
          )
        }
        className={"porTable"}
      />
    </SpaceBetween>
  );
};
